import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CampaignMapWidget from './components/CampaignMapWidget';
import TriageQueue from './components/TriageQueue';
import DataIngestionStatus from './components/DataIngestionStatus';
import ActiveInvestigations from './components/ActiveInvestigations';
import CollaborationPanel from './components/CollaborationPanel';
import CrisisMonitoring from './components/CrisisMonitoring';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ExpertConsoleDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedView, setSelectedView] = useState('overview');
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock user data for role-based access
  const currentUser = {
    id: 'user_001',
    name: 'Dr. Maria Schmidt',
    role: 'Senior Fact-Checker',
    organization: 'EFCSN Germany',
    clearanceLevel: 'expert',
    avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
    permissions: ['fact-check', 'peer-review', 'crisis-response', 'institutional-access']
  };

  // Dashboard metrics
  const dashboardMetrics = {
    activeCampaigns: 12,
    pendingReviews: 8,
    triageItems: 23,
    crisisAlerts: 3,
    collaborativeNotebooks: 6,
    dataStreamsActive: 5,
    totalReach: 15600000,
    confidenceScore: 0.84
  };

  const quickActions = [
    {
      id: 'new-investigation',
      label: 'Start Investigation',
      description: 'Begin new fact-checking workflow',
      icon: 'Search',
      color: 'bg-blue-500',
      action: () => window.location.href = '/fact-check-workbench'
    },
    {
      id: 'crisis-response',
      label: 'Crisis Response',
      description: 'Access emergency protocols',
      icon: 'AlertTriangle',
      color: 'bg-red-500',
      action: () => window.location.href = '/crisis-response-center'
    },
    {
      id: 'peer-review',
      label: 'Peer Review',
      description: 'Review pending publications',
      icon: 'Users',
      color: 'bg-green-500',
      action: () => setSelectedView('collaboration')
    },
    {
      id: 'platform-bridge',
      label: 'Platform Bridge',
      description: 'Manage platform integrations',
      icon: 'Link',
      color: 'bg-purple-500',
      action: () => window.location.href = '/platform-bridge-interface'
    }
  ];

  const viewTabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'campaigns', label: 'Campaign Mapping', icon: 'Map' },
    { id: 'triage', label: 'Triage Queue', icon: 'AlertTriangle' },
    { id: 'investigations', label: 'Investigations', icon: 'FileText' },
    { id: 'collaboration', label: 'Collaboration', icon: 'Users' },
    { id: 'crisis', label: 'Crisis Monitoring', icon: 'Shield' },
    { id: 'data', label: 'Data Streams', icon: 'Database' }
  ];

  useEffect(() => {
    if (realTimeUpdates) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [realTimeUpdates]);

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const formatLastUpdate = (date) => {
    return date?.toLocaleTimeString('en-EU', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={handleToggleSidebar}
      />
      <main className={`
        transition-all duration-300 ease-in-out pt-16
        ${sidebarCollapsed ? 'ml-16' : 'ml-64'}
      `}>
        <div className="p-6">
          {/* Dashboard Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Expert Console Dashboard</h1>
                <p className="text-muted-foreground">
                  Comprehensive monitoring and coordination for EFCSN fact-checkers
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 px-3 py-2 bg-card border border-border rounded-lg">
                  <img
                    src={currentUser?.avatar}
                    alt={currentUser?.name}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-foreground">{currentUser?.name}</div>
                    <div className="text-muted-foreground">{currentUser?.role}</div>
                  </div>
                </div>
                <Button
                  variant={realTimeUpdates ? 'default' : 'outline'}
                  size="sm"
                  iconName={realTimeUpdates ? 'Pause' : 'Play'}
                  onClick={() => setRealTimeUpdates(!realTimeUpdates)}
                >
                  {realTimeUpdates ? 'Pause Updates' : 'Resume Updates'}
                </Button>
              </div>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Activity" size={16} className="text-blue-500" />
                  <span className="text-xs text-muted-foreground">Active Campaigns</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.activeCampaigns}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-orange-500" />
                  <span className="text-xs text-muted-foreground">Pending Reviews</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.pendingReviews}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-red-500" />
                  <span className="text-xs text-muted-foreground">Triage Items</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.triageItems}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} className="text-red-600" />
                  <span className="text-xs text-muted-foreground">Crisis Alerts</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.crisisAlerts}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={16} className="text-green-500" />
                  <span className="text-xs text-muted-foreground">Notebooks</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.collaborativeNotebooks}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={16} className="text-purple-500" />
                  <span className="text-xs text-muted-foreground">Data Streams</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {dashboardMetrics?.dataStreamsActive}
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} className="text-indigo-500" />
                  <span className="text-xs text-muted-foreground">Total Reach</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {(dashboardMetrics?.totalReach / 1000000)?.toFixed(1)}M
                </div>
              </div>
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center space-x-2">
                  <Icon name="Target" size={16} className="text-teal-500" />
                  <span className="text-xs text-muted-foreground">Confidence</span>
                </div>
                <div className="text-xl font-bold text-foreground mt-1">
                  {(dashboardMetrics?.confidenceScore * 100)?.toFixed(0)}%
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {quickActions?.map((action) => (
                <button
                  key={action?.id}
                  onClick={action?.action}
                  className="p-4 bg-card rounded-lg border border-border hover:bg-muted/50 transition-smooth text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 ${action?.color} rounded-lg flex items-center justify-center`}>
                      <Icon name={action?.icon} size={20} color="white" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{action?.label}</div>
                      <div className="text-xs text-muted-foreground">{action?.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* View Tabs */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1 overflow-x-auto">
                {viewTabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setSelectedView(tab?.id)}
                    className={`
                      flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap
                      ${selectedView === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Last updated: {formatLastUpdate(lastUpdate)}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="space-y-6">
            {selectedView === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CampaignMapWidget />
                <TriageQueue />
                <ActiveInvestigations />
                <CrisisMonitoring />
              </div>
            )}

            {selectedView === 'campaigns' && (
              <div className="grid grid-cols-1 gap-6">
                <CampaignMapWidget />
              </div>
            )}

            {selectedView === 'triage' && (
              <div className="grid grid-cols-1 gap-6">
                <TriageQueue />
              </div>
            )}

            {selectedView === 'investigations' && (
              <div className="grid grid-cols-1 gap-6">
                <ActiveInvestigations />
              </div>
            )}

            {selectedView === 'collaboration' && (
              <div className="grid grid-cols-1 gap-6">
                <CollaborationPanel />
              </div>
            )}

            {selectedView === 'crisis' && (
              <div className="grid grid-cols-1 gap-6">
                <CrisisMonitoring />
              </div>
            )}

            {selectedView === 'data' && (
              <div className="grid grid-cols-1 gap-6">
                <DataIngestionStatus />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExpertConsoleDashboard;